import type { FluxErrorEntityErrorCodes } from '../../types.js';

export const AutomationV2ExecutionActionUpdateCardFieldValueCodes = {
  AutomationV2ExecutionActionUpdateCardFieldValueFailedToUpdate: {
    code: 'automation-v2-execution-action/update-card-field-value-failed-to-update',
    explanation: `Ocorreu um erro ao tentar atualizar o valor do campo no card. A operação de atualização foi executada, mas não foi concluída com sucesso.

**Causas comuns:**
- O campo especificado não existe no formulário do card ou da etapa indicada
- O valor fornecido é incompatível com o tipo do campo (ex: texto em campos de data ou número)
- O card ou a etapa não possuem permissão para edição no momento
- O campo não pertence ao formulário da etapa informada (quando \`stage_id\` é fornecido)

**Como diagnosticar:**
Verifique nos logs de execução o nome/ID do campo utilizado e o valor que foi tentado. Confirme que o campo existe no formulário correto e que o valor está no formato adequado para o tipo do campo.

**Como resolver:**
Corrija o nome/ID do campo, ajuste o valor para o formato esperado, ou remova o \`stage_id\` caso queira atualizar o formulário inicial do card.`,
    ignored: true,
    localizations: {},
    message:
      'A automação não conseguiu atualizar o valor do campo no card. Verifique se o campo existe e se o valor está no formato correto.',
    supportingDocs: [
      {
        name: 'Ação de Atualizar Campo do Card',
        path: '/wiki/docs/automations/actions/update-card-field-value',
      },
      {
        name: 'Tipos de Campos do Formulário',
        path: '/wiki/docs/pipe/forms#tipos-de-perguntas',
      },
    ],
  },
  AutomationV2ExecutionActionUpdateCardFieldValueMissingCardId: {
    code: 'automation-v2-execution-action/update-card-field-value-missing-card-id',
    explanation: `O ID do card é obrigatório para atualizar o valor de um campo. O valor do ID do card é passado automaticamente pelo gatilho ou por ações anteriores via \`context.card.id\`.

**Causas comuns:**
- A variável \`context.card.id\` não foi resolvida corretamente
- O ID do card foi digitado incorretamente no mapeamento manual
- O card não está disponível no contexto da automação

**Como resolver:**
Verifique o mapeamento do campo "ID do card" na configuração da ação. Certifique-se de que a automação possui um card disponível no contexto.`,
    ignored: true,
    localizations: {},
    message:
      'Não foi possível atualizar o campo do card porque o ID do card está faltando. Verifique se o card existe no contexto da automação ou se foi digitado corretamente.',
    supportingDocs: [
      {
        name: 'Ação de Atualizar Campo do Card',
        path: '/wiki/docs/automations/actions/update-card-field-value',
      },
      {
        name: 'Informações Dinâmicas',
        path: '/wiki/docs/automations/informacoes-dinamicas',
      },
    ],
  },
  AutomationV2ExecutionActionUpdateCardFieldValueMissingFieldNameOrId: {
    code: 'automation-v2-execution-action/update-card-field-value-missing-field-name-or-id',
    explanation: `O nome ou ID do campo é obrigatório para identificar qual campo será atualizado no card.

**Causas comuns:**
- O campo "Nome ou ID do campo" foi deixado em branco na configuração da ação
- A variável dinâmica utilizada retornou uma string vazia ou nula

**Como resolver:**
Preencha o campo "Nome ou ID do campo" com o nome exato do campo ou seu identificador único (ID). Verifique a ortografia do nome do campo no formulário do card.`,
    ignored: true,
    localizations: {},
    message:
      'O nome ou ID do campo não foi fornecido. Informe o nome ou identificador do campo que deseja atualizar.',
    supportingDocs: [
      {
        name: 'Ação de Atualizar Campo do Card',
        path: '/wiki/docs/automations/actions/update-card-field-value',
      },
      {
        name: 'Campos do Formulário',
        path: '/wiki/docs/pipe/forms',
      },
    ],
  },
} as const satisfies FluxErrorEntityErrorCodes<'automation-v2-execution-action'>;
